import { useContext, useMemo } from 'react';
import { PluginRegistryContext } from './PluginRegistry';
import { RegisteredRoute, PluginRoute, RouteRegistrationOptions, PluginRegistryType } from './types';

/**
 * Hook to access and manage plugin routes
 */
export const usePluginRoutes = () => {
  const context = useContext(PluginRegistryContext);
  
  if (!context) {
    throw new Error('usePluginRoutes must be used within a PluginRegistryProvider');
  }
  
  const {
    routes,
    registerRoute,
    unregisterRoute,
    unregisterPluginRoutes,
    getRoutes,
    getRoutesByPlugin
  }: PluginRegistryType = context;
  
  return {
    /**
     * All registered routes
     */
    routes,
    
    /**
     * Register a new route for a plugin
     */
    registerRoute,
    
    /**
     * Unregister a specific route by ID
     */
    unregisterRoute,
    
    /**
     * Unregister all routes for a specific plugin
     */
    unregisterPluginRoutes,
    
    /**
     * Get routes, optionally filtered by namespace
     */
    getRoutes,
    
    /**
     * Get routes for a specific plugin
     */
    getRoutesByPlugin,
    
    /**
     * Get routes grouped by namespace
     */
    getRoutesByNamespace: useMemo(() => {
      const grouped: Record<string, RegisteredRoute[]> = {};
      routes.forEach((route: RegisteredRoute) => {
        const namespace = route.namespace || 'default';
        if (!grouped[namespace]) {
          grouped[namespace] = [];
        }
        grouped[namespace].push(route);
      });
      return grouped;
    }, [routes]),
    
    /**
     * Get all available namespaces
     */
    getNamespaces: useMemo(() => {
      const namespaces = new Set<string>();
      routes.forEach((route: RegisteredRoute) => {
        namespaces.add(route.namespace || 'default');
      });
      return Array.from(namespaces);
    }, [routes]),
    
    /**
     * Find a route by path
     */
    findRouteByPath: (path: string): RegisteredRoute | undefined => {
      return routes.find((route: RegisteredRoute) => route.path === path);
    },
    
    /**
     * Find routes by plugin name
     */
    findRoutesByPlugin: (pluginName: string): RegisteredRoute[] => {
      return routes.filter((route: RegisteredRoute) => route.pluginName === pluginName);
    },
    
    /**
     * Check if a route exists
     */
    hasRoute: (path: string): boolean => {
      return routes.some((route: RegisteredRoute) => route.path === path);
    },
    
    /**
     * Get routes that match a pattern (simple wildcard support)
     */
    findRoutesByPattern: (pattern: string): RegisteredRoute[] => {
      const regex = new RegExp(
        '^' + pattern.replace(/\*/g, '.*').replace(/\?/g, '.') + '$'
      );
      return routes.filter((route: RegisteredRoute) => regex.test(route.path));
    }
  };
};

/**
 * Hook for plugins to easily register their routes
 */
export const usePluginRouteRegistration = (pluginName: string) => {
  const { registerRoute, unregisterRoute, unregisterPluginRoutes } = usePluginRoutes();
  
  /**
   * Register a route for this plugin
   */
  const register = (route: PluginRoute, options?: RouteRegistrationOptions) => {
    registerRoute(route, pluginName, options);
  };
  
  /**
   * Register multiple routes for this plugin
   */
  const registerMultiple = (routes: PluginRoute[], options?: RouteRegistrationOptions) => {
    routes.forEach(route => register(route, options));
  };
  
  /**
   * Unregister all routes for this plugin
   */
  const unregisterAll = () => {
    unregisterPluginRoutes(pluginName);
  };
  
  return {
    register,
    registerMultiple,
    unregister: unregisterRoute,
    unregisterAll
  };
};

/**
 * Hook to get routes in a format compatible with React Router
 */
export const useReactRouterRoutes = (namespace?: string) => {
  const { getRoutes } = usePluginRoutes();
  
  const routes = getRoutes(namespace);
  
  // Convert plugin routes to React Router format
  const reactRouterRoutes = useMemo(() => {
    return routes.map((route: RegisteredRoute) => ({
      path: route.path,
      element: route.component,
      exact: route.exact,
      children: route.children?.map((child: PluginRoute) => ({
        path: child.path,
        element: child.component,
        exact: child.exact
      }))
    }));
  }, [routes]);
  
  return reactRouterRoutes;
};

/**
 * Hook to get navigation items from registered routes
 */
export const usePluginNavigation = (namespace?: string) => {
  const { getRoutes } = usePluginRoutes();
  
  const routes = getRoutes(namespace);
  
  // Convert routes to navigation items
  const navigationItems = useMemo(() => {
    return routes
      .filter((route: RegisteredRoute) => route.title) // Only include routes with titles
      .map((route: RegisteredRoute) => ({
        path: route.path,
        title: route.title!,
        name: route.name,
        icon: route.icon,
        order: route.order || 0,
        description: route.description,
        meta: route.meta,
        pluginName: route.pluginName
      }))
      .sort((a: any, b: any) => b.order - a.order);
  }, [routes]);
  
  return navigationItems;
};

/**
 * Hook to check route guards
 */
export const useRouteGuard = () => {
  const { routes } = usePluginRoutes();
  
  /**
   * Check if access to a route is allowed
   */
  const checkAccess = async (path: string): Promise<boolean> => {
    const route = routes.find((r: RegisteredRoute) => r.path === path);
    
    if (!route || !route.guard) {
      return true; // No guard means access is allowed
    }
    
    try {
      const result = await route.guard();
      return Boolean(result);
    } catch (error) {
      console.error(`Route guard error for path ${path}:`, error);
      return false;
    }
  };
  
  /**
   * Get all routes that the user has access to
   */
  const getAccessibleRoutes = async (namespace?: string): Promise<RegisteredRoute[]> => {
    const { getRoutes } = usePluginRoutes();
    const routesToCheck = getRoutes(namespace);
    const accessibleRoutes: RegisteredRoute[] = [];
    
    for (const route of routesToCheck) {
      const hasAccess = await checkAccess(route.path);
      if (hasAccess) {
        accessibleRoutes.push(route);
      }
    }
    
    return accessibleRoutes;
  };
  
  return {
    checkAccess,
    getAccessibleRoutes
  };
};